import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { SchoolWishlistIdsResponse } from '../../api/school-wishlist/school-wishlist-ids';
import unsubscribeSchool from '../../api/school-wishlist/unsubscribe-school';
import { baseSearchKeys } from '../base-search/query-keys';
import qualityEvents from '../quality-events/quality-events';
import { schoolWishlistVotesKeys } from '../school-wishlist-votes/query-keys';
import { schoolWishlistKeys } from './query-keys';

type PreviousIdsContext = { previousIds: SchoolWishlistIdsResponse | undefined };

const useUnsubscribeSchool = (schoolId: number | null, searchRequestId?: string) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: unsubscribeSchool,
        onMutate: async ({ materialId }): Promise<PreviousIdsContext> => {
            await queryClient.cancelQueries({ queryKey: schoolWishlistKeys.ids() });

            const previousIds = queryClient.getQueryData<SchoolWishlistIdsResponse>(schoolWishlistKeys.ids());

            if (previousIds && previousIds.items.some((item) => item.id === materialId)) {
                queryClient.setQueryData<SchoolWishlistIdsResponse>(schoolWishlistKeys.ids(), {
                    items: previousIds.items.filter((item) => item.id !== materialId),
                    total: Math.max(0, previousIds.total - 1)
                });
            }

            return { previousIds };
        },
        onError: (_error, _vars, context) => {
            if (context?.previousIds) {
                queryClient.setQueryData(schoolWishlistKeys.ids(), context.previousIds);
            }
        },
        onSuccess: (_data, { materialId }) => {
            qualityEvents.schoolWishlist.removeFromSchoolWishlist({ schoolId, materialId, searchRequestId });
        },
        onSettled: (_data, _error, { materialId }) => {
            queryClient.invalidateQueries({ queryKey: schoolWishlistKeys.all });
            queryClient.invalidateQueries({ queryKey: schoolWishlistVotesKeys.voters(materialId) });
            queryClient.invalidateQueries({ queryKey: baseSearchKeys.all });
        }
    });
};

export default useUnsubscribeSchool;
